<template>
  <div class="listing-popover" @mousedown.stop>
    <template v-for="section in sectionOrder" :key="section">
    <div v-if="section === 'places' && showPlacesSection" class="listing-popover__section">
      <p class="listing-popover__label">{{ searchActive ? t('listing-search.suggest.places') : t('listing-search.suggest.popular') }}</p>
      <ul :id="`${idPrefix}-places`" class="listing-suggestions" role="listbox">
        <li
          v-if="!searchActive"
          :class="{
            'is-active': criteria.loc === nearby.label,
            'is-busy': nearbyLoading,
            'is-disabled': nearbyError
          }"
          role="option"
          :aria-selected="criteria.loc === nearby.label"
          :aria-busy="nearbyLoading"
          :aria-disabled="nearbyError"
          :tabindex="nearbyError ? -1 : 0"
          @mousedown="pickNearby"
          @keydown.enter.prevent="pickNearby({ viaKeyboard: true })"
          @keydown.space.prevent="pickNearby({ viaKeyboard: true })"
        >
          <span class="listing-suggestions__pin"><SvgIcon name="pin" :size="18" /></span>
          <span>
            {{ nearby.label }}<br />
            <span v-if="nearbyLoading" class="listing-suggestions__meta">{{ t('listing-search.nearby.loading') }}</span>
            <span v-else-if="nearbyError" class="listing-suggestions__meta listing-suggestions__meta--error">
              {{ t('listing-search.nearby.unavailable') }}
            </span>
            <span v-else class="listing-suggestions__meta">{{ nearby.region }}</span>
          </span>
        </li>
        <li
          v-for="place in placeResults"
          :id="optionId(place)"
          :key="place.slug"
          :class="{ 'is-active': criteria.loc === place.label, 'is-highlighted': highlighted === place }"
          role="option"
          :aria-selected="criteria.loc === place.label"
          tabindex="0"
          @mousedown="pickPlace(place)"
          @keydown.enter.prevent="pickPlace(place, { viaKeyboard: true })"
          @keydown.space.prevent="pickPlace(place, { viaKeyboard: true })"
        >
          <span class="listing-suggestions__pin"><SvgIcon name="pin" :size="18" /></span>
          <span>
            {{ place.label }}<br />
            <span class="listing-suggestions__meta">{{ place.sublabel }}</span>
          </span>
        </li>
        <li v-if="suggesting && !placeResults.length && !projectResults.length" role="presentation" class="listing-suggestions__empty">
          {{ t('listing-search.suggest.loading') }}
        </li>
        <li v-else-if="!placeResults.length && !projectResults.length" role="presentation" class="listing-suggestions__empty">
          {{ t('listing-search.suggest.none') }}
        </li>
      </ul>
    </div>

    <div v-else-if="section === 'projects' && projectResults.length" class="listing-popover__section">
      <p class="listing-popover__label">{{ t('listing-search.suggest.projects') }}</p>
      <ul :id="`${idPrefix}-projects`" class="listing-suggestions" role="listbox">
        <li
          v-for="project in projectResults"
          :id="optionId(project)"
          :key="project.url"
          :class="{ 'is-highlighted': highlighted === project }"
          role="option"
          :aria-selected="false"
          tabindex="0"
          @mousedown="pickProject(project)"
          @keydown.enter.prevent="pickProject(project)"
          @keydown.space.prevent="pickProject(project)"
        >
          <span class="listing-suggestions__pin"><SvgIcon name="home" :size="17" /></span>
          <span>
            {{ project.label
            }}<template v-if="project.sublabel"
              ><br /><span class="listing-suggestions__meta">{{ project.sublabel }}</span></template
            >
          </span>
        </li>
      </ul>
    </div>
    </template>

    <div class="listing-popover__section">
      <p class="listing-popover__label">{{ t('listing-search.suggest.radius') }}</p>
      <div class="listing-pills">
        <button
          v-for="km in radiusOptions"
          :key="km"
          type="button"
          class="listing-pill"
          :class="{ 'is-on': criteria.radius === km }"
          @click="update({ radius: criteria.radius === km ? null : km })"
        >
          {{ km }} km
        </button>
      </div>
    </div>

    <div class="listing-popover__footer">
      <button type="button" class="listing-link-button" @click="clear">{{ t('listing-search.clear') }}</button>
      <button
        v-if="skippable"
        type="button"
        class="listing-link-button listing-link-button--muted"
        @click="emit('next')"
      >
        {{ t('listing-search.skip') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { useCriteria } from '../lib/useCriteria';
import { t } from '../lib/i18n';
import { DEFAULT_RADIUS } from '../lib/criteriaContract';

const props = defineProps({
  api: { type: Object, required: true },
  bootstrap: { type: Object, required: true },
  // The listing pages use the shared listing criteria singleton (the default);
  // the projects page injects its own store so both pages keep their own
  // querystring contract and localStorage entry.
  criteriaStore: { type: Object, default: null },
  // The radius a fresh pick or clear falls back to; the projects page passes
  // its own 25km default.
  defaultRadius: { type: Number, default: DEFAULT_RADIUS },
  // Listing pages show places above projects; the projects page flips that.
  projectsFirst: { type: Boolean, default: false },
  // The listing search bar shows a "skip" shortcut to the next popover; the
  // projects page has no next step, so it stays hidden there.
  skippable: { type: Boolean, default: false }
});

const sectionOrder = computed(() =>
  props.projectsFirst ? ['projects', 'places'] : ['places', 'projects']
);

// With only project matches the places section would render as a bare heading;
// hide it then. It stays visible when both lists are empty because it carries
// the loading/no-results message.
const showPlacesSection = computed(
  () => !searchActive.value || placeResults.value.length > 0 || !projectResults.value.length
);

const emit = defineEmits(['pick-place', 'pick-project', 'next']);

const { criteria, update } = props.criteriaStore || useCriteria();

// A committed place carries coordinates; treat loc as its label, not a pending
// search term, so reopening shows the popular list instead of re-running suggest
// (which would report "not found" for an already chosen place). Typing clears the
// coordinates, which turns loc back into a live query.
const query = computed(() => {
  if (criteria.long !== null && criteria.lat !== null) return '';
  return (criteria.loc || '').trim();
});
// Mirrors SuggestionService::MINIMUM_QUERY_LENGTH: the API answers empty below
// this anyway, so skipping the request changes nothing except saving the call.
// Below the minimum the popover keeps showing the popular list instead of a
// premature "not found".
const MINIMUM_QUERY_LENGTH = 2;
const searchActive = computed(() => query.value.length >= MINIMUM_QUERY_LENGTH);
const results = ref([]);
const suggesting = ref(false);

// Measured 2026-08-28 (WHN-2133): /web-api/suggest answers in 29 to 36ms on a
// warm cache and 125 to 172ms cold, so the debounce was the dominant part of the
// wait, not the lookup. 150ms still collapses a burst of keystrokes into one
// request while putting the first suggestions well inside 300ms.
const SUGGEST_DEBOUNCE_MS = 150;

let debounceTimer = null;
// One request at a time. The debounce alone only collapses keystrokes inside its
// own window, so typing at a steady pace still starts a request per keystroke;
// WebApiRateLimitSubscriber allows 120 per client IP per minute across all of
// /web-api/, and an office or mobile network shares one IP over its visitors.
// Keystrokes that land while a request runs collapse into a single follow-up for
// whatever the query is once it settles, which also makes out-of-order responses
// impossible instead of merely ignored.
let inFlight = false;
let disposed = false;

const runSuggest = async () => {
  const q = query.value;
  if (inFlight || disposed || q.length < MINIMUM_QUERY_LENGTH) return;

  inFlight = true;
  let landed;
  try {
    landed = (await props.api.suggest(q)).results;
  } catch {
    landed = [];
  }
  inFlight = false;

  if (disposed) return;

  // The query moved on while this ran, so this answer is not the one to paint.
  // Below the minimum length the watcher already cleared the list and the
  // follow-up bows out; otherwise it fetches what the field now holds.
  if (query.value !== q) {
    runSuggest();
    return;
  }

  results.value = landed;
  suggesting.value = false;
};

watch(
  query,
  (q) => {
    clearTimeout(debounceTimer);
    if (q.length < MINIMUM_QUERY_LENGTH) {
      results.value = [];
      suggesting.value = false;
      return;
    }
    suggesting.value = true;
    debounceTimer = setTimeout(runSuggest, SUGGEST_DEBOUNCE_MS);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearTimeout(debounceTimer);
  disposed = true;
});

const nearby = computed(() => props.bootstrap.nearby);

const popularAsResults = computed(() =>
  props.bootstrap.popularPlaces.slice(0, 3).map((place) => ({
    kind: 'place',
    slug: place.slug,
    label: place.label,
    sublabel: place.region,
    long: place.lon,
    lat: place.lat
  }))
);

const placeResults = computed(() => {
  if (!searchActive.value) return popularAsResults.value;
  return results.value.filter((result) => result.kind !== 'project');
});

const projectResults = computed(() =>
  searchActive.value ? results.value.filter((result) => result.kind === 'project') : []
);

const radiusOptions = computed(() => props.bootstrap.radiusOptions);

// `next` says whether the step came from the keyboard: the mobile sheet only
// moves focus on then, since focusing the price input after a tap would pop
// up the on-screen keyboard over the sheet.
const pickPlace = (place, { viaKeyboard = false } = {}) => {
  update({ loc: place.label, long: place.long ?? null, lat: place.lat ?? null, radius: props.defaultRadius });
  emit('pick-place', place);
  emit('next', { viaKeyboard });
};

// "In de buurt": geolocate the visitor and send those coordinates. When
// geolocation is unavailable or denied nothing is filled in - an inline message
// is shown instead, so the field never claims a location we do not have.
const nearbyLoading = ref(false);
const nearbyError = ref(false);

const pickNearby = ({ viaKeyboard = false } = {}) => {
  if (nearbyLoading.value || nearbyError.value) return;

  if (!navigator.geolocation) {
    nearbyError.value = true;
    return;
  }

  nearbyLoading.value = true;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      nearbyLoading.value = false;
      const { label, region } = nearby.value;
      update({ loc: label, long: position.coords.longitude, lat: position.coords.latitude, radius: props.defaultRadius });
      emit('pick-place', { kind: 'place', slug: 'nearby', label, sublabel: region });
      emit('next', { viaKeyboard });
    },
    () => {
      nearbyLoading.value = false;
      nearbyError.value = true;
    }
  );
};

const pickProject = (project) => {
  emit('pick-project', project);
};

const clear = () => {
  update({ loc: '', long: null, lat: null, radius: props.defaultRadius });
  emit('pick-place', null);
};

const idPrefix = useId();

const listboxId = computed(() =>
  [showPlacesSection.value && `${idPrefix}-places`, projectResults.value.length && `${idPrefix}-projects`]
    .filter(Boolean)
    .join(' ')
);

// The suggestions in rendered section order, the list the arrow keys walk.
// Only for a live query; the popular/nearby list is click-only because Enter
// there would geolocate.
const orderedResults = computed(() => {
  if (!searchActive.value) return [];
  return props.projectsFirst
    ? [...projectResults.value, ...placeResults.value]
    : [...placeResults.value, ...projectResults.value];
});

const highlightIndex = ref(0);

watch(orderedResults, () => {
  highlightIndex.value = 0;
});

// Enter picks exactly this item, so the marker only shows once the list it
// points into has settled.
const highlighted = computed(() =>
  suggesting.value ? null : orderedResults.value[highlightIndex.value] ?? null
);

const optionId = (item) => {
  const index = orderedResults.value.indexOf(item);
  return index === -1 ? undefined : `${idPrefix}-option-${index}`;
};

const activeId = computed(() => (highlighted.value ? optionId(highlighted.value) : null));

const moveHighlight = async (delta) => {
  if (!highlighted.value) return;
  highlightIndex.value = Math.min(
    orderedResults.value.length - 1,
    Math.max(0, highlightIndex.value + delta)
  );
  await nextTick();
  const option = document.getElementById(activeId.value);
  if (option) option.scrollIntoView({ block: 'nearest' });
};

// Without a live query a committed place has nothing left to pick, so Enter
// moves on to the next step instead.
const pickHighlighted = () => {
  if (!searchActive.value) {
    if (criteria.long !== null && criteria.lat !== null) emit('next', { viaKeyboard: true });
    return;
  }
  const item = highlighted.value;
  if (!item) return;
  if (item.kind === 'project') {
    pickProject(item);
  } else {
    pickPlace(item, { viaKeyboard: true });
  }
};

defineExpose({ pickHighlighted, moveHighlight, listboxId, activeId });
</script>
